import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export default function InstanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  const { data } = useQuery({
    queryKey: ['instance', id],
    queryFn: () => api.instances.get(id),
  });

  const createMeetingMutation = useMutation({
    mutationFn: (data) => api.meetings.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['instance', id]);
      setShowCreateMeeting(false);
      setTitle('');
      setScheduledAt('');
    },
  });

  const handleCreateMeeting = (e) => {
    e.preventDefault();
    const scheduled = new Date(scheduledAt).getTime() / 1000;
    createMeetingMutation.mutate({
      instance_id: id,
      title,
      scheduled_at: scheduled,
    });
  };

  const instance = data?.instance;
  const meetings = data?.meetings || [];
  const members = data?.members || [];

  if (!instance) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/instances')}
            className="text-blue-600 hover:underline mb-2"
          >
            ← Retour aux instances
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{instance.name}</h1>
          <p className="text-gray-600">{instance.description}</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Réunions</h2>
              <button
                onClick={() => setShowCreateMeeting(!showCreateMeeting)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {showCreateMeeting ? 'Annuler' : 'Nouvelle réunion'}
              </button>
            </div>

            {showCreateMeeting && (
              <div className="bg-white p-6 rounded-lg shadow mb-6">
                <form onSubmit={handleCreateMeeting} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Titre
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date et heure
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Créer
                  </button>
                </form>
              </div>
            )}

            <div className="space-y-4">
              {meetings.map((meeting) => (
                <div
                  key={meeting.id}
                  onClick={() => navigate(`/meetings/${meeting.id}`)}
                  className="bg-white p-6 rounded-lg shadow hover:shadow-md cursor-pointer transition"
                >
                  <h3 className="text-lg font-semibold mb-2">
                    {meeting.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {new Date(meeting.scheduled_at * 1000).toLocaleString(
                      'fr-FR'
                    )}
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                    {meeting.status}
                  </span>
                </div>
              ))}

              {meetings.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Aucune réunion planifiée
                </div>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Membres</h2>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="space-y-3">
                {members.map((member) => (
                  <div key={member.id} className="border-b pb-2 last:border-0">
                    <p className="font-medium">Utilisateur {member.user_id.substring(0, 8)}</p>
                    <p className="text-sm text-gray-600">
                      {member.role_in_instance}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
